import { useEffect, useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import '../../css/Order.css'
import { onErrorImg, plusComma } from '../../js/script'
// import { BtnBlack } from '../Button'
// import { useCommonContext } from '../provider/CommonProvider'
import { useAppContext } from '../provider/AppProvider'
import { useOrderContext } from '../provider/OrderProvider'
import { AFWebLinkType, RNPostMessage } from '../../js/postMessage'
import { FlattenOrderProduct } from './OrderProductSection'
import { OrderSheetCoupon } from '@shopby/shop-sdk'

type OrderProductItemProps = {
  item: FlattenOrderProduct
  productCoupons?: OrderSheetCoupon[] // 쿠폰 정보
  disableCouponButton?: boolean // 쿠폰 버튼 비활성화 여부
  onClickCouponButton?: (productNo: number) => void // 쿠폰 버튼 클릭 핸들러
}

const OrderProductItem = ({
  item,
  productCoupons,
  disableCouponButton,
  onClickCouponButton,
}: OrderProductItemProps) => {
  const { orderProductOption } = item
  const [totalPrice, setTotalPrice] = useState<number>(orderProductOption.price.buyAmt)
  const [discountPercent, setDiscountPercent] = useState<number>(0)

  const navigate = useNavigate()
  const { isAppView } = useAppContext()
  const { productCoupon } = useOrderContext()
  const appliedCoupon = useMemo(
    () => productCoupon[`${item.productNo}_${item.orderProductOption.optionNo}`] || null,
    [productCoupon, item.orderProductOption.optionNo]
  )

  const originDiscountPercent = useMemo(() => {
    return Math.floor((orderProductOption.price.immediateDiscountAmt / orderProductOption.price.salePrice) * 100)
  }, [orderProductOption.price.immediateDiscountAmt, orderProductOption.price.salePrice])

  const onLinkClickHandler = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    if (isAppView) {
      RNPostMessage(AFWebLinkType.moveToLink, {
        url: `aboutfishing://aboutfishing.com/?page=webview&url=${process.env.REACT_APP_SHOP_URL}/product/${item.productNo}`,
      })
    } else {
      navigate(`/product/${item.productNo}`)
    }
  }

  useEffect(() => {
    setTotalPrice(orderProductOption.price.buyAmt)
    const getPercent = Math.floor(
      (orderProductOption.price.immediateDiscountAmt / orderProductOption.price.salePrice) * 100
    )
    setDiscountPercent(getPercent)
  }, [item])

  // 쿠폰 적용가 계산
  const couponDiscountAmt = appliedCoupon ? Number(appliedCoupon.couponDiscountAmt || 0) : 0
  const couponAppliedPrice = orderProductOption.price.buyAmt - couponDiscountAmt

  return (
    <ItemWrap>
      <Row>
        <ImageArea>
          <StyledLink to={`/product/${item.productNo}`} onClick={onLinkClickHandler}>
            <ImageDim />
            <ProductImg src={item.imageUrl} alt='productImage' onError={onErrorImg} />
          </StyledLink>
        </ImageArea>
        <InfoArea to={`/product/${item.productNo}`} onClick={onLinkClickHandler}>
          <BrandTitle>{item.brandName}</BrandTitle>
          <ProductTitle>{item.productName}</ProductTitle>
          <OptionText>옵션 : {orderProductOption.optionTitle}</OptionText>
          <AmountText>수량 : {orderProductOption.orderCnt}개</AmountText>
          {appliedCoupon && (
            <AppliedCoupon>
              적용 쿠폰: {appliedCoupon.couponName} ({plusComma(appliedCoupon.couponDiscountAmt)}원 할인)
            </AppliedCoupon>
          )}
          <DiscountWrap>
            {/* 원래 상품가(정가) */}
            {orderProductOption.price.standardAmt > 0 && (
              <SalePrice>{plusComma(orderProductOption.price.standardAmt)}원</SalePrice>
            )}

            {/* 즉시할인 퍼센트와 즉시할인 적용가 */}
            <DiscountPriceInfo>
              {originDiscountPercent > 0 && <Percent>{originDiscountPercent}%</Percent>}
              <DiscountPrice>{plusComma(orderProductOption.price.buyAmt)}원</DiscountPrice>
            </DiscountPriceInfo>

            {/* PC: 쿠폰 적용가를 옆에 표시 */}
            {appliedCoupon && (
              <CouponAppliedPrice className='coupon-pc'>
                쿠폰적용가 <span>{plusComma(couponAppliedPrice)}원</span>
                <CouponDiscount>(-{plusComma(couponDiscountAmt)}원)</CouponDiscount>
              </CouponAppliedPrice>
            )}
          </DiscountWrap>

          {/* 모바일: 쿠폰 적용가를 할인가 아래에 별도로 표시 */}
          {appliedCoupon && (
            <CouponAppliedPrice className='coupon-mobile'>
              쿠폰적용가 <span>{plusComma(couponAppliedPrice)}원</span>
              <CouponDiscount>(-{plusComma(couponDiscountAmt)}원)</CouponDiscount>
            </CouponAppliedPrice>
          )}
        </InfoArea>
      </Row>
      {productCoupons && productCoupons.length > 0 && (
        <CouponButton
          className='coupon_button'
          disabled={disableCouponButton}
          onClick={() => onClickCouponButton?.(item.productNo)}
        >
          쿠폰 적용
        </CouponButton>
      )}
    </ItemWrap>
  )
}

const ItemWrap = styled.div`
  width: 100%;
  padding: 20px 0;
  border-radius: 12px;
  background: #fff;
  margin-bottom: 12px;
  position: relative;

  @media (min-width: 1126px) {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 14px;

  & > .coupon_button {
    margin-left: auto;
    align-self: flex-start;
  }

  @media (max-width: 1125px) {
    gap: 10px;
    & > .coupon_button {
      margin-left: 0;
      margin-top: 12px;
      width: 100%;
      align-self: stretch;
    }
  }
  @media (max-width: 1125px) {
    /* CouponButton PC용 숨김, 모바일용 보임 */
    .coupon_button:not(.coupon-mobile) {
      display: none;
    }
  }
`

const ImageArea = styled.div`
  width: 134px;
  height: 134px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  position: relative;
  flex-shrink: 0;
  background: #f8f8f8;
  overflow: hidden;

  @media (max-width: 1125px) {
    width: 30%;
    height: auto;
    min-width: 80px;
    min-height: 80px;
    padding-bottom: 30%;
  }
`

const ImageDim = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  z-index: 1;
`

const ProductImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  background: #f5f5f5;
  z-index: 2;
  position: relative;

  @media (max-width: 1125px) {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 8px;
  }
`

const StyledLink = styled(Link)`
  display: block;
  width: 100%;
  height: 100%;
`

const InfoArea = styled(Link)`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-decoration: none;
  color: inherit;

  @media (max-width: 1125px) {
    width: 80%;
  }
  @media (max-width: 600px) {
    width: 100%;
  }
`

const BrandTitle = styled.p`
  font-size: 13px;
  color: #888;
  margin: 0 0 2px 0;
`

const ProductTitle = styled.p`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 2px 0;
  color: #222;
`

const OptionText = styled.p`
  font-size: 13px;
  color: #555;
  margin: 0 0 2px 0;
`

const AmountText = styled.p`
  font-size: 13px;
  color: #555;
  margin: 0 0 6px 0;
`

const AppliedCoupon = styled.div`
  font-size: 13px;
  color: #1769aa;
  background: #e3f2fd;
  border-radius: 4px;
  padding: 4px 8px;
  margin-bottom: 4px;
  display: inline-block;
`

const DiscountWrap = styled.div`
  margin-top: 4px;
  display: flex;
  align-items: baseline;
  gap: 8px;
`

const SalePrice = styled.p`
  font-size: 13px;
  color: #bbb;
  text-decoration: line-through;
  margin: 0;
`

const DiscountPriceInfo = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`

const Percent = styled.p`
  font-size: 15px;
  color: #ff3d00;
  font-weight: 600;
  margin: 0;
`

const DiscountPrice = styled.p`
  font-size: 18px;
  color: #222;
  font-weight: 700;
  margin: 0;
`

const CouponAppliedPrice = styled.div`
  margin-left: 12px;
  font-size: 14px;
  color: #1769aa;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  span {
    color: #1769aa;
    font-weight: 700;
    margin-left: 2px;
  }

  &.coupon-mobile {
    display: none;
    margin-left: 0;
    margin-top: 2px;
  }

  @media (max-width: 1250px) {
    &.coupon-pc {
      display: none;
    }
    &.coupon-mobile {
      display: flex;
    }
  }
  @media (max-width: 950px) {
    font-size: 11px;
  }
`
const CouponDiscount = styled.span`
  color: #ff3d00;
  font-size: 13px;
  font-weight: 500;
  margin-left: 4px;
  @media (max-width: 950px) {
    font-size: 11px;
  }
`

const CouponButton = styled.button<{ disabled?: boolean }>`
  &.coupon_button {
    background: ${({ disabled }) => (disabled ? '#ddd' : '#222')};
    color: ${({ disabled }) => (disabled ? '#aaa' : '#fff')};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    margin-left: 16px;
    margin-top: 0;
    transition: background 0.2s;
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
    height: 40px;
    align-self: flex-start;
    min-width: 110px;
  }
  &.coupon-mobile {
    display: none;
  }
  @media (max-width: 1125px) {
    &.coupon_button {
      width: 100%;
      margin-left: 0;
      margin-top: 12px;
      align-self: stretch;
      height: auto;
    }
    &.coupon-mobile {
      display: block;
    }
    /* Row 안 coupon_button 숨김 */
    &:not(.coupon-mobile) {
      display: none;
    }
  }
`

export default OrderProductItem
